import { documentService, useUser } from "@/app/providers/services";
import { isLockedByOther } from "@/lib/user";
import { ProjectWriterGuard } from "@/auth/ComponentGuard";
import { autoFormatIgnoreTimezone } from "@/common/util";
import { useNotify } from "@/common/notifications";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Dialog, { useDialog } from "@imminently/decisively-ui/Dialog";
import Form from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import TextArea from "@imminently/decisively-ui/TextArea";
import TextField from "@imminently/decisively-ui/TextField";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import Avatar from "@imminently/decisively-ui/Avatar";
import type { Document } from "@packages/commons";
import { get } from "lodash";
import { type PropsWithChildren, useCallback } from "react";
import { useTranslation } from "react-i18next";

const initialMetadata = (document: Document): DocumentMetadataValues => ({
  source: document.metadata?.source ?? "",
  dateOfIssuance: document.metadata?.dateOfIssuance ?? "",
  notes: document.metadata?.notes ?? "",
  documentType: document.metadata?.documentType ?? "",
  jurisdiction: document.metadata?.jurisdiction ?? "",
  status: document.metadata?.status ?? "",
  language: document.metadata?.language ?? "",
  confidentiality: document.metadata?.confidentiality ?? "",
});

type DocumentMetadataValues = {
  source: string;
  dateOfIssuance: string;
  notes: string;
  documentType: string;
  jurisdiction: string;
  status: string;
  language: string;
  confidentiality: string;
};

const UserInfoCard = ({ name, children }: { name?: string } & PropsWithChildren) => {
  return (
    <div className="flex flex-row flex-nowrap items-center gap-2">
      <Tooltip title={name ?? ""}>
        <Avatar
          alt={name}
          name={name}
          size="md"
        />
      </Tooltip>
      <div className="flex flex-col">{children}</div>
    </div>
  );
};

const useLockState = (name: string) => {
  const user = useUser();
  // in order for us to have opened the document, the release has to already exist, so pull from there
  const release = useFullReleaseStrict();
  const path = name.split("/").filter(Boolean);
  const document = get(release.documents, path);
  return {
    lock: document?.locked,
    readOnly: isLockedByOther(document?.locked),
  };
};

const DocumentMetadataModal = ({ document, close }: { document: Document; close: () => void }) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { readOnly } = useLockState(document.name);
  const { mutateAsync, isLoading } = documentService.useUpdate();
  const handleSubmit = async (values: DocumentMetadataValues) => {
    try {
      const metadata = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ""));
      await mutateAsync({
        id: document.id,
        payload: { metadata },
      });
      close();
    } catch (e) {
      console.error(e);
      notify.error(t("errors.failed_update_metadata"));
    }
  };

  return (
    <Dialog.Content className="flex max-h-[90vh] w-full max-w-3xl flex-col">
      <DialogHeader title={t("metadata.metadata_title")} />
      <Form<DocumentMetadataValues>
        defaultValues={initialMetadata(document)}
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <Dialog.Body asChild>
          <FormPanel className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex items-center justify-start *:flex-1">
              <UserInfoCard name={document.createdByName}>
                <h6 className="text-sm font-semibold">{t("created_at")}</h6>
                <span className="text-xs text-muted-foreground">{autoFormatIgnoreTimezone(document.created)}</span>
              </UserInfoCard>
              <UserInfoCard name={document.updatedByName}>
                <h6 className="text-sm font-semibold">{t("updated_at")}</h6>
                <span className="text-xs text-muted-foreground">{autoFormatIgnoreTimezone(document.lastModified)}</span>
              </UserInfoCard>
            </div>
            <TextField
              disabled={readOnly}
              name="source"
              label={t("metadata.source")}
              helperText={t("metadata.source_description")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="dateOfIssuance"
              label={t("metadata.date_of_issuance")}
              helperText={t("metadata.date_of_issuance_description")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="documentType"
              label={t("metadata.document_type")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="jurisdiction"
              label={t("metadata.jurisdiction")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="status"
              label={t("metadata.status")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="language"
              label={t("metadata.language")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="confidentiality"
              label={t("metadata.confidentiality")}
              fullWidth
            />
            <TextField
              disabled={readOnly}
              name="notes"
              label={t("metadata.notes")}
              inputComponent={TextArea}
              rows={3}
              fullWidth
            />
          </FormPanel>
        </Dialog.Body>
        <ProjectWriterGuard>
          <Dialog.Actions>
            <Dialog.Actions.PrimaryButton
              label={t("update")}
              type="submit"
              loading={isLoading}
              disabled={isLoading || readOnly}
            />
          </Dialog.Actions>
        </ProjectWriterGuard>
      </Form>
    </Dialog.Content>
  );
};

export const useDocumentMetadataModal = () => {
  const { pushDialog } = useDialog();
  return useCallback(
    async (documentId: string) => {
      const document = await documentService.getOne(documentId);
      pushDialog({
        layer: WORKSPACE_LAYER,
        options: { intent: "form" },
        component: ({ close }) => (
          <DocumentMetadataModal
            document={document as Document}
            close={close}
          />
        ),
      });
    },
    [pushDialog],
  );
};
